/**
 * 3PL usage report: pure, in-memory shaping of the ONE
 * `reporting.getThreePlUsage` response.
 *
 * No React and no i18n. Everything here returns numbers, ids, colours and
 * literal translation keys that the components translate, so the whole page
 * (client switch, bill-line filter, search, sort, chart bucketing) runs over
 * the full response on one layer and never costs a second call.
 *
 * Money (step 2): every amount comes from the response, in the response's
 * `currency`, and every sum here runs in whole cents (D47) so a total never
 * drifts from the server's `toBill`.
 */
import { THREE_PL_BILL_NOTE_MAX, THREE_PL_BILL_QTY_MAX, toCents } from '@threadheads/shared';
import type { RouterOutputs } from '@/lib/trpc';
import { normalizeNumericText } from '@/lib/parse-kg';
import { dateFnsLocaleFor, formatDayMonth } from '@/lib/format-dates';
import { updateSearchParams } from '@/lib/search-params';
// Only the URL module of Client prices: it is small and already in the main
// bundle. Its grid state module stays in the lazy Client prices chunk.
import {
  builtinItemKey,
  serviceItemKey,
  type ClientPricesTab,
  type PriceFocus,
} from '@/features/client-prices/lib/client-prices-url';

export type ThreePlUsageReport = RouterOutputs['reporting']['getThreePlUsage'];
export type ThreePlUsageRow = ThreePlUsageReport['rows'][number];
export type ThreePlUsageClient = ThreePlUsageReport['clients'][number];
export type ThreePlUsageDay = ThreePlUsageReport['daily'][number];
export type ThreePlUsageViewer = ThreePlUsageReport['viewer'];
export type ThreePlUsageCharge = ThreePlUsageReport['charges'][number];
export type ThreePlUsageGarment = ThreePlUsageReport['garments'][number];
export type ThreePlUsageBillLine = ThreePlUsageReport['lines'][number];
export type ThreePlUsageUnpriced = NonNullable<ThreePlUsageReport['unpricedSkus']>[number];

// ---------------------------------------------------------------------------
// Colour (DESIGN.md §13.11): a client colour is a chart key only, always
// beside the client's name. Assigned by the client's index in `clients[]`
// (server order, by name) so a client keeps one colour on every part of the
// page. Two purples, then slate steps: no second accent.
// ---------------------------------------------------------------------------

export const CLIENT_COLOURS = ['#7E69AB', '#C3B5DB', '#64748b', '#94a3b8', '#475569'] as const;

/**
 * "Other clients" in the total bar and the chart, and "Extra services" among
 * one bill's money parts. Never in the client cycle.
 */
export const OTHER_COLOUR = '#cbd5e1';

/**
 * "Not shipped, still charged" among one bill's money parts (DESIGN.md
 * §13.11): slate-700, outside `CLIENT_COLOURS`, `KIND_COLOURS` and
 * `OTHER_COLOUR`. Dark between two light parts, so it separates in the bar.
 */
export const NOT_SHIPPED_COLOUR = '#334155';

/**
 * Kinds inside one client. Kept out of the client cycle so a dot never reads
 * as a kind. The money parts of one bill reuse the same ramp (D56): Printing
 * `printed`, Pick and pack `pickPack`, {owner} stock used `blank`.
 */
export const KIND_COLOURS = {
  printed: '#59446F',
  pickPack: '#9A86C0',
  blank: '#D2C8E5',
} as const;

export function clientColour(index: number): string {
  const size = CLIENT_COLOURS.length;
  return CLIENT_COLOURS[((index % size) + size) % size] ?? CLIENT_COLOURS[0];
}

// ---------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------

/**
 * Longest period the report accepts, in inclusive days. LOCKSTEP with the S0
 * guard in supabase/migrations/20261010150000_blu770_3pl_usage_report.sql
 * (`p_end_date - p_start_date > 365` raises 22023): change both together.
 * Past it the tab shows "period too long" and makes no call.
 */
export const THREE_PL_USAGE_MAX_DAYS = 366;

/**
 * One bar per day up to this many days, then one bar per Monday-start week.
 * 366 day-bars in a card about 700px wide are under 2px each and cannot be
 * hovered; 53 week bars keep the shape a bill review needs.
 */
export const DAY_BARS_MAX_DAYS = 62;

const DAY_MS = 24 * 60 * 60 * 1000;

/** UTC midnight of a `YYYY-MM-DD` calendar day (the browser zone never shifts it). */
export function dayToUtcMs(iso: string): number {
  const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

export function utcMsToDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Inclusive number of calendar days from `from` to `to` (UTC math). */
export function periodDays(from: string, to: string): number {
  return Math.round((dayToUtcMs(to) - dayToUtcMs(from)) / DAY_MS) + 1;
}

/**
 * THE day formatter of the tab (ticks, tooltip, the screen-reader list, bill date lines,
 * toasts): day and short month through the app's one date module
 * (`lib/format-dates.ts`, DESIGN.md §17) in the active date-fns locale, "4 Oct"
 * or "10月4日". The server's `YYYY-MM-DD` parses as local midnight of that day,
 * so the browser's zone never moves it. A period label carries the year once,
 * on its end: `formatPeriodEnds`.
 */
export function formatUsageDay(iso: string, language: string): string {
  return formatDayMonth(iso, dateFnsLocaleFor(language));
}

const todayFormatters = new Map<string, Intl.DateTimeFormat>();

function todayFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = todayFormatters.get(timeZone);
  if (cached) return cached;
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    // RangeError: a zone the browser does not know reads as UTC, the server's fallback.
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' });
  }
  todayFormatters.set(timeZone, formatter);
  return formatter;
}

/**
 * The location's calendar day at `now`, `YYYY-MM-DD` (D27): the day a line
 * added now bills on. Computed in the browser from `report.location.timezone`
 * (S3: the report carries no `today`); an unknown zone is UTC. The add's
 * returned `chargeDate`, stamped in SQL, stays the truth for the toast.
 */
export function localToday(timeZone: string, now: Date): string {
  const parts = todayFormatter(timeZone).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** A `YYYY-MM-DD` day inside an inclusive period. */
export function isDayInPeriod(day: string, period: { from: string; to: string }): boolean {
  return period.from <= day && day <= period.to;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** Every money sum here runs in whole cents (D47), through the one `toCents`. */
function sumCents(values: Iterable<number | null>): number {
  let cents = 0;
  for (const value of values) cents += value === null ? 0 : toCents(value);
  return cents;
}

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

/** One client's entries of a response list; an absent (owner-only) list is empty. */
function byClient<T extends { clientOrgId: string }>(list: readonly T[] | undefined, clientId: string): T[] {
  return (list ?? []).filter((entry) => entry.clientOrgId === clientId);
}

// ---------------------------------------------------------------------------
// Rows, cells, kinds
// ---------------------------------------------------------------------------

/** The one cell formatter for the clients table, the bill and the SKU table
 *  (shared with Settings > Billing's NumberCells). */
export { formatCountCell as cell } from '@/lib/format-count';

/** What happened to a shipped piece: the SKU table's count columns and the chart's kinds. */
export type KindId = 'printed' | 'pickPack' | 'blank';

const KIND_KEYS: Record<
  KindId,
  {
    own: 'printedOwn' | 'pickPackOwn' | 'blankOwn';
    borrowed: 'printedBorrowed' | 'pickPackBorrowed' | 'blankBorrowed';
  }
> = {
  printed: { own: 'printedOwn', borrowed: 'printedBorrowed' },
  pickPack: { own: 'pickPackOwn', borrowed: 'pickPackBorrowed' },
  blank: { own: 'blankOwn', borrowed: 'blankBorrowed' },
};

/** "12 printed" and so on: the kinds on the 3PL's stock under a bill line. */
const KIND_COUNT_KEYS: Record<KindId, string> = {
  printed: 'reporting.threePlUsage.count.printed',
  pickPack: 'reporting.threePlUsage.count.pickPack',
  blank: 'reporting.threePlUsage.count.blank',
};

/** Pieces of one kind on a SKU row, both stocks. */
export function kindQty(row: ThreePlUsageRow, kind: KindId): number {
  const keys = KIND_KEYS[kind];
  return row[keys.own] + row[keys.borrowed];
}

/** Pieces shipped on the 3PL's stock (the three shipped "Borrowed" keys). */
export function ownerStockOf(row: ThreePlUsageRow): number {
  return row.printedBorrowed + row.pickPackBorrowed + row.blankBorrowed;
}

/** "Not shipped, still charged": printed then cancelled plus artwork file errors. */
export function notShippedOf(row: ThreePlUsageRow): number {
  return (
    row.cancelledPrintedOwn +
    row.cancelledPrintedBorrowed +
    row.artworkErrorOwn +
    row.artworkErrorBorrowed
  );
}

/**
 * Rows with a non-zero billable count. A row that exists only because of a
 * production redo feeds the owner's totals, never the SKU table or the CSV.
 */
export function billableRows(rows: readonly ThreePlUsageRow[]): ThreePlUsageRow[] {
  return rows.filter((row) => row.shipped > 0 || notShippedOf(row) > 0);
}

export interface StockPart {
  /** `client`: the client's own stock. `owner`: the 3PL's stock. */
  party: 'client' | 'owner';
  count: number;
}

/** Non-zero parts only, the client part first; empty when there is nothing. */
export function stockParts(clientCount: number, ownerCount: number): StockPart[] {
  const parts: StockPart[] = [];
  if (clientCount > 0) parts.push({ party: 'client', count: clientCount });
  if (ownerCount > 0) parts.push({ party: 'owner', count: ownerCount });
  return parts;
}

// ---------------------------------------------------------------------------
// Bill lines (D20): each line's id is also its `uline` filter value
// ---------------------------------------------------------------------------

export const BILL_LINES = [
  'printing',
  'pickPack',
  'ownerStock',
  'cancelledPrint',
  'cancelledGarment',
  'artworkPrint',
  'artworkGarment',
] as const;
export type BillLineId = (typeof BILL_LINES)[number];

/** Lines priced by Printing or Pick and pack, read from `charges` (one row per price in force, D21). */
type ChargeLineId = 'printing' | 'pickPack' | 'cancelledPrint' | 'artworkPrint';
/** The 3PL's garment, read from `garments` ("By SKU", never split). */
type GarmentLineId = 'ownerStock' | 'cancelledGarment' | 'artworkGarment';

/** Step 1 `uline` values still open the line they became (D20); `blank` has no line now. */
const LEGACY_LINES: Readonly<Record<string, BillLineId | null>> = {
  printed: 'printing',
  cancelled: 'cancelledPrint',
  artwork: 'artworkPrint',
  blank: null,
};

/** A `uline` value as a line id, step 1 values aliased; null when unknown. */
export function parseBillLine(value: string | null | undefined): BillLineId | null {
  if (value == null) return null;
  if ((BILL_LINES as readonly string[]).includes(value)) return value as BillLineId;
  return LEGACY_LINES[value] ?? null;
}

/** Translation key of each bill line title (literal, so the i18n ratchet sees it). `{{owner}}` where named. */
export const LINE_TITLE_KEYS: Record<BillLineId, string> = {
  printing: 'reporting.threePlUsage.line.printing',
  pickPack: 'reporting.threePlUsage.pickPack',
  ownerStock: 'reporting.threePlUsage.line.ownerStock',
  cancelledPrint: 'reporting.threePlUsage.bill.cancelled',
  cancelledGarment: 'reporting.threePlUsage.line.cancelledGarment',
  artworkPrint: 'reporting.threePlUsage.bill.artwork',
  artworkGarment: 'reporting.threePlUsage.line.artworkGarment',
};

/** Pieces a SKU row puts on a line: the SKU table filter keeps rows above 0. */
const LINE_COUNT: Record<BillLineId, (row: ThreePlUsageRow) => number> = {
  printing: (row) => kindQty(row, 'printed'),
  pickPack: (row) => row.shipped,
  ownerStock: ownerStockOf,
  cancelledPrint: (row) => row.cancelledPrintedOwn + row.cancelledPrintedBorrowed,
  // Every cancelled piece on the 3PL's stock: the rows carry no "decorated"
  // flag (D19), so a film-only cancel still lists its SKU here.
  cancelledGarment: (row) => row.cancelledPrintedBorrowed,
  artworkPrint: (row) => row.artworkErrorOwn + row.artworkErrorBorrowed,
  artworkGarment: (row) => row.artworkErrorBorrowed,
};

export function lineCount(row: ThreePlUsageRow, line: BillLineId): number {
  return LINE_COUNT[line](row);
}

/** One client's rows with that bill line above 0. */
export function lineFilter(
  rows: readonly ThreePlUsageRow[],
  clientId: string,
  line: BillLineId,
): ThreePlUsageRow[] {
  return rows.filter((row) => row.clientOrgId === clientId && lineCount(row, line) > 0);
}

/**
 * Back to bill lands on the line you came from (P1): the bill's Set price adds
 * `ufocus=<anchor>` to the report URL it hands Client prices as `billReturnTo`,
 * and the report consumes it once (removed with `replace`) when the bill is on
 * screen. The anchor is a LINE, never a price row: a saved price can re-split
 * a line's rows, so row keys are not stable across the trip. A built-in line's
 * id (`printing`), `serviceOn:<serviceId>` for a turned-on service,
 * `line:<lineId>` for a hand-added line. The tab matches it against each
 * `data-bill-anchor` through `dataset`, so no selector is built from the URL.
 */
export const BILL_FOCUS_PARAM = 'ufocus';

/** The report URL Set price hands Client prices: every param kept, plus `ufocus`. */
export function billReturnUrl(pathname: string, search: string, anchor: string): string {
  const params = updateSearchParams(new URLSearchParams(search), { [BILL_FOCUS_PARAM]: anchor });
  return `${pathname}?${params.toString()}`;
}

/** The Services tab row a built-in line's Set price lands on (D24). */
const BUILTIN_ITEM_KEYS: Record<ChargeLineId, string> = {
  printing: builtinItemKey('printing'),
  pickPack: builtinItemKey('pick_pack'),
  cancelledPrint: builtinItemKey('printing'),
  artworkPrint: builtinItemKey('printing'),
};

/** Grey lines under a bill line's title. Never a number in a number cell. */
export type BillDetail =
  /** A fixed sentence. */
  | { kind: 'text'; key: string }
  /** A sentence with a count (plural by `count`; `{{owner}}` where named). */
  | { kind: 'count'; key: string; count: number }
  /** The stock split, "306 on Threadheads stock, 12 on Thread Lab stock"; empty = "None this period". */
  | { kind: 'split'; parts: StockPart[] }
  /** Kinds on the 3PL's stock, "12 printed, 3 pick and pack"; empty = "None this period". */
  | { kind: 'kinds'; parts: Array<{ key: string; count: number }> };

/** One value per cell (D22): a price in force, "By SKU" on a garment row, or "-". */
export type BillPrice = { kind: 'unit'; value: number } | { kind: 'bySku' } | { kind: 'none' };

/** Where Set price lands (`buildSetPriceHref`): a garment's priceless SKUs, or a service row. */
export type SetPriceTarget =
  | {
      tab: Extract<ClientPricesTab, 'products'>;
      skus: string[];
      /** Present (true) only when the server listed fewer priceless SKUs than the line has (`skuCount`, D25). */
      capped?: true;
    }
  | { tab: Extract<ClientPricesTab, 'services'>; focus: PriceFocus };

/**
 * Garment rows: priceless pieces on SKUs no longer on Client prices (SM6: a
 * blank the 3PL retired or stopped offering, or another org's SKU). Set price
 * cannot reach them, so the bill says why instead of linking.
 */
export interface BillNotOffered {
  pieces: number;
  /** How many such SKUs the server listed. */
  skuCount: number;
  /** The code when it is exactly one SKU with a code, else null (the bill gives a count). */
  sku: string | null;
}

/** Owner `skus.costs_manage` holders only (D28): what has no price on this row, and how to fix it. */
export interface BillUnpriced {
  /**
   * Charge rows: null ("No price set"). Garment rows: the priceless pieces Set
   * price can reach (the server's total, never the capped list's length, less
   * `notOffered`); 0 when every priceless piece is on a SKU no longer offered.
   */
  pieces: number | null;
  /**
   * Null when nothing listed can be priced on Client prices (SM6), or when the
   * row's service is archived (D33: the Services grid lists active services
   * only): the warning shows without the link.
   */
  setPrice: SetPriceTarget | null;
  /** Garment rows only, and only when some of its priceless SKUs are no longer offered (C6). */
  notOffered?: BillNotOffered;
}

/** One bill row: Qty x one Price = Amount (D21). */
export interface BillRowView {
  /** Unique within the bill. */
  key: string;
  qty: number;
  price: BillPrice;
  /** 0 shows "-" (a missing price is 0 here; `unpriced` carries the warning). */
  amount: number;
  /** Set when the line has 2 or more price rows: the days this price covered. */
  dateRange: { from: string; to: string } | null;
  unpriced: BillUnpriced | null;
}

export interface BillLineView {
  id: BillLineId;
  titleKey: string;
  /** Under the title of the line's first row only (they describe the whole line). */
  details: BillDetail[];
  /** The line's total pieces: above 0 makes it a filter toggle. */
  qty: number;
  /**
   * One row per price in force; a line with nothing has one empty row (kept in
   * `lines` by id, but never in a section: P3 leaves Qty 0 lines off the bill).
   */
  rows: BillRowView[];
}

/** The grey line under an Extra services row's title. */
export type BillExtraDetail =
  /** "Every order shipped while turned on" / "Every piece shipped while turned on" (beside its days, C1). */
  | { kind: 'turnedOn'; chargePer: 'piece' | 'order' }
  /** A hand-added line's note, as typed (the client sees it, D36). */
  | { kind: 'note'; text: string }
  /** A hand-added line left without a note: "Added by hand". */
  | { kind: 'byHand' };

/**
 * An Extra services row (D3, D4, D36): the turned-on services first (one row
 * per price in force, D21), then the hand-added lines. Rows are plain: never a
 * filter toggle.
 */
export interface BillExtraRowView {
  /** Unique within the bill. */
  key: string;
  /**
   * Where Back to bill lands (`BILL_FOCUS_PARAM`): `serviceOn:<serviceId>`
   * (every price row of a turned-on service shares it) or `line:<lineId>`.
   */
  anchor: string;
  serviceId: string;
  /** The service's own name, from the charge or line itself (S3). */
  serviceName: string;
  chargePer: 'piece' | 'order' | 'flat';
  /** A hand-added line's id (the only rows that can be removed); null for a turned-on service. */
  lineId: string | null;
  note: string | null;
  qty: number;
  price: BillPrice;
  /** 0 shows "-" (a missing price is 0 here; `unpriced` carries the warning). */
  amount: number;
  /**
   * The days this row covers: always set. A turned-on service row names its
   * first to last charge day (a small extension of D21, C1): it charges only
   * from its turn-on day, so "every piece shipped while turned on" needs its
   * days beside it. A hand-added line names its one billing day (D3), so two
   * lines of the same service can be told apart before one is removed.
   */
  dateRange: { from: string; to: string } | null;
  /** Under the title of a service's first row and of every hand-added line; null on later price rows. */
  detail: BillExtraDetail | null;
  unpriced: BillUnpriced | null;
}

export interface BillView {
  /** Every line by id; the sections below hold these same objects. */
  lines: Record<BillLineId, BillLineView>;
  sections: {
    shipped: BillLineView[];
    notShipped: BillLineView[];
    extra: BillExtraRowView[];
  };
  /**
   * Extra services shows when it has a row, and for an owner Admin always (it
   * holds Add a service to this bill). Never a heading over nothing (P3).
   */
  showExtra: boolean;
  /** Pieces shipped (the bill header). */
  pieces: number;
  /** The bill Total: the client's `toBill`. */
  total: number;
  /**
   * Cancelled pieces on the 3PL's stock whose garment is NOT charged: only
   * their DTF film was printed (D19). "Printed, then cancelled" counts them;
   * the cancelled-garment line does not, and says so (C2). When that line is
   * left out (none decorated), "Printed, then cancelled" says it instead.
   */
  cancelledFilmOnly: number;
}

export interface BillOptions {
  /** Owner viewer AND `skus.manage` (D28): the only caller who sees no-price warnings and Set price. */
  canFix: boolean;
}

interface BillContext {
  viewer: ThreePlUsageViewer;
  canFix: boolean;
}

/**
 * D22. Owner: a null price is "-" (the warning is separate); a deliberate 0
 * is shown. Client: a 0, deliberate or missing, is "-", so it never tells.
 */
function chargePrice(unitPrice: number | null, viewer: ThreePlUsageViewer): BillPrice {
  if (unitPrice === null) return { kind: 'none' };
  if (viewer === 'client' && unitPrice === 0) return { kind: 'none' };
  return { kind: 'unit', value: unitPrice };
}

const EMPTY_ROW = { qty: 0, price: { kind: 'none' } as BillPrice, amount: 0, dateRange: null, unpriced: null };

function chargeRows(line: ChargeLineId, charges: readonly ThreePlUsageCharge[], context: BillContext): BillRowView[] {
  const list = charges
    .filter((charge) => charge.line === line)
    .sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to));
  if (list.length === 0) return [{ key: line, ...EMPTY_ROW }];
  const versioned = list.length > 1;
  return list.map((charge, index) => ({
    key: `${line}:${index}`,
    qty: charge.qty,
    price: chargePrice(charge.unitPrice, context.viewer),
    amount: charge.amount ?? 0,
    dateRange: versioned ? { from: charge.from, to: charge.to } : null,
    unpriced:
      context.canFix && charge.unitPrice === null
        ? {
            pieces: null,
            setPrice: { tab: 'services', focus: { kind: 'service', itemKey: BUILTIN_ITEM_KEYS[line] } },
          }
        : null,
  }));
}

function garmentRows(
  line: GarmentLineId,
  garments: readonly ThreePlUsageGarment[],
  unpriced: readonly ThreePlUsageUnpriced[],
  context: BillContext,
): BillRowView[] {
  const garment = garments.find((entry) => entry.line === line);
  const qty = garment?.qty ?? 0;
  const missing = context.canFix ? unpriced.find((entry) => entry.line === line) : undefined;
  return [
    {
      key: line,
      qty,
      price: qty > 0 ? { kind: 'bySku' } : { kind: 'none' },
      amount: garment?.amount ?? 0,
      dateRange: null,
      unpriced: missing ? garmentUnpriced(missing) : null,
    },
  ];
}

/**
 * A garment row's warning, split in two (C6): the pieces Set price can reach
 * (SKUs still on Client prices, plus any the server did not list past its cap
 * of 50, D25), and the listed pieces on SKUs no longer offered (SM6), which
 * get a reason instead of a link.
 */
function garmentUnpriced(missing: ThreePlUsageUnpriced): BillUnpriced {
  // Only SKUs still on Client prices can be priced there (SM6); a null code is another org's SKU.
  const offered = missing.skus.flatMap((sku) => (sku.offered && sku.sku !== null ? [sku.sku] : []));
  const notOffered = missing.skus.filter((sku) => !(sku.offered && sku.sku !== null));
  const notOfferedPieces = notOffered.reduce((sum, sku) => sum + sku.pieces, 0);
  // The server lists at most 50 priceless SKUs per line (D25): the link says so.
  const setPrice: SetPriceTarget | null =
    offered.length > 0
      ? missing.skuCount > missing.skus.length
        ? { tab: 'products', skus: offered, capped: true }
        : { tab: 'products', skus: offered }
      : null;
  const [only] = notOffered;
  return {
    pieces: Math.max(0, missing.pieces - notOfferedPieces),
    setPrice,
    ...(notOffered.length > 0
      ? {
          notOffered: {
            pieces: notOfferedPieces,
            skuCount: notOffered.length,
            sku: notOffered.length === 1 ? (only?.sku ?? null) : null,
          },
        }
      : {}),
  };
}

/** Set price on a service row: the Services tab, focused on that service's row (D24). */
function serviceSetPrice(serviceId: string): SetPriceTarget {
  return { tab: 'services', focus: { kind: 'service', itemKey: serviceItemKey(serviceId) } };
}

type TurnedOnCharge = ThreePlUsageCharge & {
  serviceId: string;
  serviceName: string;
  chargePer: 'piece' | 'order';
};

function isTurnedOn(charge: ThreePlUsageCharge): charge is TurnedOnCharge {
  return (
    charge.line === 'serviceOn' &&
    charge.serviceId !== null &&
    charge.serviceName !== null &&
    charge.chargePer !== null
  );
}

/**
 * Extra services: each turned-on service (by name, its price rows in date
 * order, D21), then the hand-added lines in the order they bill (D3). Same
 * price and warning rules as the charge rows (D22, D24). `activeServiceIds`
 * are this client's `billServices`: a row whose service is not among them was
 * archived (D33) and has no row on the Services grid, so it warns without a
 * Set price link.
 */
function extraRows(
  charges: readonly ThreePlUsageCharge[],
  lines: readonly ThreePlUsageBillLine[],
  activeServiceIds: ReadonlySet<string>,
  context: BillContext,
): BillExtraRowView[] {
  const byService = new Map<string, TurnedOnCharge[]>();
  for (const charge of charges) {
    if (!isTurnedOn(charge)) continue;
    const list = byService.get(charge.serviceId);
    if (list) list.push(charge);
    else byService.set(charge.serviceId, [charge]);
  }
  const unpriced = (serviceId: string, unitPrice: number | null): BillUnpriced | null =>
    context.canFix && unitPrice === null
      ? { pieces: null, setPrice: activeServiceIds.has(serviceId) ? serviceSetPrice(serviceId) : null }
      : null;

  const turnedOn = [...byService.values()]
    .map((list) => list.sort((a, b) => a.from.localeCompare(b.from) || a.to.localeCompare(b.to)))
    .sort((a, b) => {
      const [first] = a;
      const [second] = b;
      if (!first || !second) return 0;
      return first.serviceName.localeCompare(second.serviceName) || first.serviceId.localeCompare(second.serviceId);
    })
    .flatMap((list) =>
      list.map((charge, index): BillExtraRowView => ({
        key: `serviceOn:${charge.serviceId}:${index}`,
        anchor: `serviceOn:${charge.serviceId}`,
        serviceId: charge.serviceId,
        serviceName: charge.serviceName,
        chargePer: charge.chargePer,
        lineId: null,
        note: null,
        qty: charge.qty,
        price: chargePrice(charge.unitPrice, context.viewer),
        amount: charge.amount ?? 0,
        dateRange: { from: charge.from, to: charge.to },
        detail: index === 0 ? { kind: 'turnedOn', chargePer: charge.chargePer } : null,
        unpriced: unpriced(charge.serviceId, charge.unitPrice),
      })),
    );

  const byHand = [...lines]
    .sort(
      (a, b) =>
        a.chargeDate.localeCompare(b.chargeDate) ||
        a.serviceName.localeCompare(b.serviceName) ||
        a.id.localeCompare(b.id),
    )
    .map((line): BillExtraRowView => ({
      key: `line:${line.id}`,
      anchor: `line:${line.id}`,
      serviceId: line.serviceId,
      serviceName: line.serviceName,
      chargePer: line.chargePer,
      lineId: line.id,
      note: line.note,
      qty: line.qty,
      price: chargePrice(line.unitPrice, context.viewer),
      amount: line.amount ?? 0,
      dateRange: { from: line.chargeDate, to: line.chargeDate },
      detail: line.note !== null && line.note.trim() !== '' ? { kind: 'note', text: line.note } : { kind: 'byHand' },
      unpriced: unpriced(line.serviceId, line.unitPrice),
    }));

  return [...turnedOn, ...byHand];
}

function sumBy(rows: readonly ThreePlUsageRow[], pick: (row: ThreePlUsageRow) => number): number {
  return rows.reduce((sum, row) => sum + pick(row), 0);
}

function lineView(id: BillLineId, details: BillDetail[], rows: BillRowView[]): BillLineView {
  return {
    id,
    titleKey: LINE_TITLE_KEYS[id],
    details,
    qty: rows.reduce((sum, row) => sum + row.qty, 0),
    rows,
  };
}

/**
 * One client's priced bill: every line by id (D20), each with one row per
 * price in force (D21), then each section picks its lines by id, so no reader
 * depends on a line's position. One empty-row rule (P3): a line with Qty 0 is
 * left out of its section, in every section, and the bill lists only what it
 * charges. A section with no lines is left out; Extra services stays for an
 * owner Admin, who adds services there.
 */
export function buildBill(data: ThreePlUsageReport, clientId: string, options: BillOptions): BillView {
  // A client viewer never gets a fix control, whatever the caller passes.
  const context: BillContext = { viewer: data.viewer, canFix: options.canFix && data.viewer === 'owner' };
  const rows = byClient(data.rows, clientId);
  const charges = byClient(data.charges, clientId);
  const garments = byClient(data.garments, clientId);
  const unpriced = byClient(data.unpricedSkus, clientId);
  const activeServiceIds = new Set(byClient(data.billServices, clientId).map((service) => service.id));
  const extra = extraRows(charges, byClient(data.lines, clientId), activeServiceIds, context);

  const split = (own: number, borrowed: number): BillDetail => ({ kind: 'split', parts: stockParts(own, borrowed) });
  const kinds = (['printed', 'pickPack', 'blank'] as const).flatMap((kind) => {
    const count = sumBy(rows, (row) => row[KIND_KEYS[kind].borrowed]);
    return count > 0 ? [{ key: KIND_COUNT_KEYS[kind], count }] : [];
  });
  const cancelled = {
    own: sumBy(rows, (r) => r.cancelledPrintedOwn),
    borrowed: sumBy(rows, (r) => r.cancelledPrintedBorrowed),
  };
  const artwork = {
    own: sumBy(rows, (r) => r.artworkErrorOwn),
    borrowed: sumBy(rows, (r) => r.artworkErrorBorrowed),
  };
  const withSplit = (key: string, counts: { own: number; borrowed: number }): BillDetail[] =>
    counts.own + counts.borrowed > 0
      ? [{ kind: 'text', key }, split(counts.own, counts.borrowed)]
      : [{ kind: 'text', key }];
  // D19: a cancelled piece charges the 3PL's garment only when the garment was
  // decorated; a DTF film printed then cancelled charges the print only. The
  // rows carry no "decorated" flag, so the gap is the line's own difference.
  const cancelledGarmentRows = garmentRows('cancelledGarment', garments, unpriced, context);
  const cancelledGarmentQty = cancelledGarmentRows.reduce((sum, row) => sum + row.qty, 0);
  const cancelledFilmOnly = Math.max(0, cancelled.borrowed - cancelledGarmentQty);
  // The sentence sits on the cancelled-garment line while that line shows.
  // At Qty 0 the line is left out of the bill (below), so when every cancel
  // on the 3PL's stock was film-only, "Printed, then cancelled" carries it.
  const filmOnly = (key: string): BillDetail[] =>
    cancelledFilmOnly > 0 ? [{ kind: 'count', key, count: cancelledFilmOnly }] : [];

  const lines: Record<BillLineId, BillLineView> = {
    printing: lineView(
      'printing',
      [split(sumBy(rows, (r) => r.printedOwn), sumBy(rows, (r) => r.printedBorrowed))],
      chargeRows('printing', charges, context),
    ),
    pickPack: lineView(
      'pickPack',
      [{ kind: 'text', key: 'reporting.threePlUsage.line.pickPackDetail' }],
      chargeRows('pickPack', charges, context),
    ),
    ownerStock: lineView(
      'ownerStock',
      [{ kind: 'kinds', parts: kinds }],
      garmentRows('ownerStock', garments, unpriced, context),
    ),
    cancelledPrint: lineView(
      'cancelledPrint',
      [
        ...withSplit('reporting.threePlUsage.line.cancelledPrintDetail', cancelled),
        ...(cancelledGarmentQty === 0 ? filmOnly('reporting.threePlUsage.line.cancelledPrintFilmOnly') : []),
      ],
      chargeRows('cancelledPrint', charges, context),
    ),
    cancelledGarment: lineView(
      'cancelledGarment',
      [
        { kind: 'text', key: 'reporting.threePlUsage.line.cancelledGarmentDetail' },
        ...(cancelledGarmentQty > 0 ? filmOnly('reporting.threePlUsage.line.cancelledGarmentFilmOnly') : []),
      ],
      cancelledGarmentRows,
    ),
    artworkPrint: lineView(
      'artworkPrint',
      withSplit('reporting.threePlUsage.bill.artworkDetail', artwork),
      chargeRows('artworkPrint', charges, context),
    ),
    artworkGarment: lineView(
      'artworkGarment',
      [{ kind: 'text', key: 'reporting.threePlUsage.line.artworkGarmentDetail' }],
      garmentRows('artworkGarment', garments, unpriced, context),
    ),
  };

  const charged = (line: BillLineView) => line.qty > 0;
  return {
    lines,
    sections: {
      shipped: [lines.printing, lines.pickPack, lines.ownerStock].filter(charged),
      notShipped: [lines.cancelledPrint, lines.cancelledGarment, lines.artworkPrint, lines.artworkGarment].filter(
        charged,
      ),
      extra,
    },
    showExtra: extra.length > 0 || context.canFix,
    pieces: sumBy(rows, (row) => row.shipped),
    total: data.clients.find((client) => client.id === clientId)?.toBill ?? 0,
    cancelledFilmOnly,
  };
}

/**
 * Owner `skus.costs_manage` holders only (D28): the bill's LINES with no
 * price, counted on the bill itself, so the count is exactly the amber
 * warnings such an owner sees on it (C4). One unit everywhere it is counted (the clients table, the total
 * panel's "Not final" note and the bill): a line, because a line is the one
 * thing every warning has. Pieces would not do: a per-order service counts
 * orders, a flat fee counts times, and a charge row's warning has no count.
 * 0 for a bill built without `canFix`.
 */
export function noPriceLines(bill: BillView): number {
  const rows = [...Object.values(bill.lines).flatMap((line) => line.rows), ...bill.sections.extra];
  return rows.filter((row) => row.unpriced !== null).length;
}

// ---------------------------------------------------------------------------
// Add a service to this bill (owner `skus.costs_manage` holders only, D28)
// ---------------------------------------------------------------------------

export type ThreePlBillService = NonNullable<ThreePlUsageReport['billServices']>[number];

/** The 3PL's own active services for one client's bill, by name; never Printing or Pick and pack. */
export function billServicesFor(data: ThreePlUsageReport, clientId: string): ThreePlBillService[] {
  return byClient(data.billServices, clientId)
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

export type BillQtyParse =
  | { kind: 'ok'; value: number }
  | { kind: 'empty' }
  /** Not a whole number of at least 1 (D48). */
  | { kind: 'invalid' }
  /** A whole number past the cap (D48). */
  | { kind: 'tooMany' };

/**
 * The hand-added quantity (D48): a whole number from 1 to 100,000. Chinese
 * keyboards type full-width digits; they fold through the one numeric map.
 */
export function parseBillQty(text: string): BillQtyParse {
  const s = normalizeNumericText(text);
  if (s === '') return { kind: 'empty' };
  if (!/^\d+$/.test(s)) return { kind: 'invalid' };
  const value = Number(s);
  if (value < 1) return { kind: 'invalid' };
  if (value > THREE_PL_BILL_QTY_MAX) return { kind: 'tooMany' };
  return { kind: 'ok', value };
}

/** True when more typing could still make the text a valid quantity (DESIGN.md §9 commit rule). */
export function billQtyCanBecomeValid(text: string): boolean {
  return /^\d*$/.test(normalizeNumericText(text));
}

/** The note as sent: trimmed, null when empty (D36); `tooLong` past 200 characters. */
export function parseBillNote(text: string): { kind: 'ok'; value: string | null } | { kind: 'tooLong' } {
  const trimmed = text.trim();
  if (trimmed.length > THREE_PL_BILL_NOTE_MAX) return { kind: 'tooLong' };
  return { kind: 'ok', value: trimmed === '' ? null : trimmed };
}

// ---------------------------------------------------------------------------
// Segments and chart series
// ---------------------------------------------------------------------------

/** A part of the total bar. `name` comes from the response; `labelKey` is ours. */
export interface UsageSegment {
  id: string;
  name: string | null;
  labelKey: string | null;
  colour: string;
  /** Money, in the response's currency. */
  value: number;
}

export interface ChartSeries {
  /** recharts dataKey. */
  dataKey: string;
  name: string | null;
  labelKey: string | null;
  colour: string;
  /** Daily entries of these clients roll into the series. */
  clientIds: readonly string[];
  /** One kind of those clients' daily entries, or every kind when null. */
  kind: KindId | null;
}

/** A total-bar part or chart series label: the response's name, else our translated key. */
export function partLabel(
  part: { name: string | null; labelKey: string | null },
  t: (key: string) => string,
): string {
  return part.name ?? (part.labelKey ? t(part.labelKey) : '');
}

/** Never more than this many CLIENT parts in the total bar and the chart (one bill has its five money parts). */
export const MAX_PARTS = 4;

const KINDS: ReadonlyArray<{ id: KindId; labelKey: string }> = [
  { id: 'printed', labelKey: 'reporting.threePlUsage.printed' },
  { id: 'pickPack', labelKey: 'reporting.threePlUsage.pickPack' },
  { id: 'blank', labelKey: 'reporting.threePlUsage.blank' },
];

/**
 * One bill's money parts, always these five in this order: the bill's own
 * sections (D56, DESIGN.md §13.11). Shipped splits into its three lines; Not
 * shipped, still charged and Extra services are one part each.
 */
const MONEY_PARTS = [
  { id: 'printing', labelKey: 'reporting.threePlUsage.line.printing', colour: KIND_COLOURS.printed },
  { id: 'pickPack', labelKey: 'reporting.threePlUsage.pickPack', colour: KIND_COLOURS.pickPack },
  { id: 'ownerStock', labelKey: 'reporting.threePlUsage.line.ownerStock', colour: KIND_COLOURS.blank },
  { id: 'notShipped', labelKey: 'reporting.threePlUsage.notShipped', colour: NOT_SHIPPED_COLOUR },
  { id: 'extra', labelKey: 'reporting.threePlUsage.bill.section.extra', colour: OTHER_COLOUR },
] as const;

interface ClientGroup {
  id: string;
  name: string | null;
  labelKey: string | null;
  colour: string;
  clientIds: string[];
  /** Money to bill, in whole cents (summed for Other clients). */
  cents: number;
}

/**
 * Up to 4 clients: one part each. More: the 3 largest BILLS, each in its own
 * index colour, plus "Other clients" holding the rest. The tab is a bill, so
 * money ranks (P4); ties go to pieces shipped (so deploy day, every bill $0,
 * still ranks sensibly), then `clients[]` order. Ranked ONCE: the total bar
 * and the pieces-per-day chart share these groups, so a client is never in
 * its own colour in one and in "Other clients" in the other (DESIGN.md
 * §13.11). Parts keep the `clients[]` order; Other comes last.
 */
function clientGroups(
  clients: readonly ThreePlUsageClient[],
  rank: ReadonlyMap<string, { cents: number; pieces: number }>,
): ClientGroup[] {
  const own = clients.map((client, index) => ({
    id: client.id,
    name: client.name,
    labelKey: null,
    colour: clientColour(index),
    clientIds: [client.id],
    cents: rank.get(client.id)?.cents ?? 0,
    pieces: rank.get(client.id)?.pieces ?? 0,
    index,
  }));
  const strip = ({ index: _index, pieces: _pieces, ...group }: (typeof own)[number]): ClientGroup => group;
  if (own.length <= MAX_PARTS) return own.map(strip);
  const largest = new Set(
    [...own]
      .sort((a, b) => b.cents - a.cents || b.pieces - a.pieces || a.index - b.index)
      .slice(0, MAX_PARTS - 1)
      .map((group) => group.id),
  );
  const rest = own.filter((group) => !largest.has(group.id));
  return [
    ...own.filter((group) => largest.has(group.id)).map(strip),
    {
      id: 'other',
      name: null,
      labelKey: 'reporting.threePlUsage.total.otherClients',
      colour: OTHER_COLOUR,
      clientIds: rest.map((group) => group.id),
      cents: rest.reduce((sum, group) => sum + group.cents, 0),
    },
  ];
}

// ---------------------------------------------------------------------------
// Chart buckets
// ---------------------------------------------------------------------------

export interface DayTotal {
  date: string;
  total: number;
}

export interface UsageBucket {
  /** First day of the bucket (also its x key). */
  start: string;
  /** Last day of the bucket (equal to start in day mode). */
  end: string;
  total: number;
  /** Pieces per series dataKey. */
  values: Record<string, number>;
}

export interface UsageBuckets {
  mode: 'day' | 'week';
  /** Every day of the period, gaps filled with 0. */
  days: DayTotal[];
  buckets: UsageBucket[];
}

function emptyValues(series: readonly ChartSeries[]): Record<string, number> {
  const values: Record<string, number> = {};
  for (const s of series) values[s.dataKey] = 0;
  return values;
}

/**
 * Fill the gaps and group, in one pass over `daily[]` (days with nothing are
 * absent from the response). Day mode up to DAY_BARS_MAX_DAYS, else Monday-start
 * weeks clipped to the period. All calendar math is on UTC days.
 */
export function buildBuckets(
  from: string,
  to: string,
  daily: readonly ThreePlUsageDay[],
  series: readonly ChartSeries[],
): UsageBuckets {
  const startMs = dayToUtcMs(from);
  const count = Math.max(0, periodDays(from, to));
  const dates: string[] = [];
  const dayValues: Array<Record<string, number>> = [];
  const indexByDate = new Map<string, number>();
  for (let i = 0; i < count; i += 1) {
    const date = utcMsToDay(startMs + i * DAY_MS);
    dates.push(date);
    dayValues.push(emptyValues(series));
    indexByDate.set(date, i);
  }

  const memberships = series.map((s) => ({ series: s, ids: new Set(s.clientIds) }));
  for (const entry of daily) {
    const index = indexByDate.get(entry.date);
    const values = index === undefined ? undefined : dayValues[index];
    if (!values) continue;
    for (const { series: s, ids } of memberships) {
      if (!ids.has(entry.clientOrgId)) continue;
      values[s.dataKey] =
        (values[s.dataKey] ?? 0) +
        (s.kind ? entry[s.kind] : entry.printed + entry.pickPack + entry.blank);
    }
  }

  const sumValues = (values: Record<string, number>) =>
    Object.values(values).reduce((sum, n) => sum + n, 0);
  const days: DayTotal[] = dates.map((date, i) => ({
    date,
    total: sumValues(dayValues[i] ?? {}),
  }));

  if (count <= DAY_BARS_MAX_DAYS) {
    return {
      mode: 'day',
      days,
      buckets: dates.map((date, i) => ({
        start: date,
        end: date,
        total: days[i]?.total ?? 0,
        values: dayValues[i] ?? emptyValues(series),
      })),
    };
  }

  const buckets: UsageBucket[] = [];
  let current: UsageBucket | null = null;
  for (let i = 0; i < count; i += 1) {
    const date = dates[i] ?? utcMsToDay(startMs + i * DAY_MS);
    const isMonday = new Date(startMs + i * DAY_MS).getUTCDay() === 1;
    if (current === null || isMonday) {
      current = { start: date, end: date, total: 0, values: emptyValues(series) };
      buckets.push(current);
    }
    current.end = date;
    for (const [key, n] of Object.entries(dayValues[i] ?? {})) {
      current.values[key] = (current.values[key] ?? 0) + n;
    }
    current.total += days[i]?.total ?? 0;
  }
  return { mode: 'week', days, buckets };
}

// Whole numbers only: the axis counts pieces. The small steps keep a quiet
// period (busiest day 4 pieces) on a tight axis instead of a flat 0 to 25.
const NICE_STEPS = [1, 2, 5, 10, 25, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

/**
 * Y-axis ticks: 0 and multiples of the smallest round step that gives at most
 * 5 gridlines above 0, the last at or above `max` (always at least one step,
 * so an empty period still draws 0 and 1).
 */
export function niceStep(max: number): number[] {
  const target = Math.max(0, max);
  let step = NICE_STEPS.find((candidate) => Math.ceil(target / candidate) <= 5);
  if (step === undefined) {
    let base = 10000;
    while (step === undefined) {
      for (const factor of [2, 5, 10]) {
        if (Math.ceil(target / (base * factor)) <= 5) {
          step = base * factor;
          break;
        }
      }
      base *= 10;
    }
  }
  const lines = Math.max(1, Math.ceil(target / step));
  return Array.from({ length: lines + 1 }, (_, i) => i * step);
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export interface ClientRowView {
  id: string;
  name: string;
  colour: string;
  orders: number;
  pieces: number;
  ownerStock: number;
  notShipped: number;
  /** Production redos (the 3PL's cost); null for a client viewer. */
  redone: number | null;
  /** The client's bill total (money). */
  toBill: number;
  /** Lines on this client's bill with no price (`noPriceLines`): owner `skus.costs_manage` holders only (D28), else null. */
  noPrice: number | null;
}

export interface ClientTotalsView {
  orders: number;
  pieces: number;
  ownerStock: number;
  notShipped: number;
  redone: number | null;
  toBill: number;
}

export interface ScopedClient {
  id: string;
  name: string;
  colour: string;
  orders: number;
}

export interface UsageChart extends UsageBuckets {
  series: ChartSeries[];
}

export interface UsageView {
  viewer: ThreePlUsageViewer;
  scope: 'all' | 'one';
  /** The client of the bill (one-client scope), else null. */
  client: ScopedClient | null;
  clientsById: ReadonlyMap<string, { name: string; colour: string }>;
  /** Every row of the scope (production-redo-only rows included). */
  scopeRows: ThreePlUsageRow[];
  /** Nothing in the scope at all: no row, no charge and no hand-added line. */
  empty: boolean;
  /** Pieces shipped in the scope. */
  total: number;
  /** Money to bill in the scope: the total panel's big number. */
  amount: number;
  /** Pieces shipped on the 3PL's stock in the scope. */
  ownerStock: number;
  orders: number;
  topUps: number;
  /** Production redos in the scope (owner only), else null. */
  redone: number | null;
  /** Money parts of `amount`. */
  segments: UsageSegment[];
  /**
   * Lines with no price on the bill(s) in scope, so `amount` is not final:
   * owner `skus.costs_manage` holders only (D28, C3), else null. All clients:
   * the sum of the
   * clients table's counts; one client: that bill's.
   */
  noPrice: number | null;
  clientRows: ClientRowView[] | null;
  footer: ClientTotalsView | null;
  bill: BillView | null;
  chart: UsageChart;
}

function redoOf(row: ThreePlUsageRow): number {
  return row.discardedProductionRedo ?? 0;
}

function clientsByIdOf(data: ThreePlUsageReport): Map<string, { name: string; colour: string }> {
  return new Map(
    data.clients.map((client, index) => [
      client.id,
      { name: client.name, colour: clientColour(index) },
    ]),
  );
}

/**
 * Resolve the client the page is scoped to: a client viewer always sees its
 * own bill; an owner sees the picked client when it is one of its clients,
 * else All clients.
 */
export function resolveScopeClientId(
  data: ThreePlUsageReport,
  selectedClientId: string | null,
): string | null {
  if (data.viewer === 'client') return data.clients[0]?.id ?? null;
  if (selectedClientId && data.clients.some((client) => client.id === selectedClientId)) {
    return selectedClientId;
  }
  return null;
}

const NO_FIX: BillOptions = { canFix: false };

/** All clients (3PL only): one row per client, a footer of column sums, parts by client. */
export function buildAllClientsView(data: ThreePlUsageReport, options: BillOptions = NO_FIX): UsageView {
  const isOwner = data.viewer === 'owner';
  const canFix = options.canFix && isOwner;
  const rowsByClient = new Map<string, ThreePlUsageRow[]>();
  for (const row of data.rows) {
    const list = rowsByClient.get(row.clientOrgId);
    if (list) list.push(row);
    else rowsByClient.set(row.clientOrgId, [row]);
  }
  const clientRows: ClientRowView[] = data.clients.map((client, index) => {
    const rows = rowsByClient.get(client.id) ?? [];
    return {
      id: client.id,
      name: client.name,
      colour: clientColour(index),
      orders: client.ordersShipped,
      pieces: sumBy(rows, (row) => row.shipped),
      ownerStock: sumBy(rows, ownerStockOf),
      notShipped: sumBy(rows, notShippedOf),
      redone: isOwner ? sumBy(rows, redoOf) : null,
      toBill: client.toBill,
      noPrice: canFix ? noPriceLines(buildBill(data, client.id, { canFix })) : null,
    };
  });
  const amountCents = sumCents(data.clients.map((client) => client.toBill));
  const footer: ClientTotalsView = {
    orders: clientRows.reduce((sum, row) => sum + row.orders, 0),
    pieces: clientRows.reduce((sum, row) => sum + row.pieces, 0),
    ownerStock: clientRows.reduce((sum, row) => sum + row.ownerStock, 0),
    notShipped: clientRows.reduce((sum, row) => sum + row.notShipped, 0),
    redone: isOwner ? clientRows.reduce((sum, row) => sum + (row.redone ?? 0), 0) : null,
    toBill: amountCents / 100,
  };
  // One ranking for the bar and the chart (P4): by money, ties by pieces.
  const groups = clientGroups(
    data.clients,
    new Map(clientRows.map((row) => [row.id, { cents: toCents(row.toBill), pieces: row.pieces }])),
  );
  const series: ChartSeries[] = groups.map((group, index) => ({
    dataKey: group.id === 'other' ? 'other' : `s${index}`,
    name: group.name,
    labelKey: group.labelKey,
    colour: group.colour,
    clientIds: group.clientIds,
    kind: null,
  }));
  return {
    viewer: data.viewer,
    scope: 'all',
    client: null,
    clientsById: clientsByIdOf(data),
    scopeRows: data.rows,
    empty: data.rows.length === 0 && data.charges.length === 0 && data.lines.length === 0,
    total: footer.pieces,
    amount: footer.toBill,
    ownerStock: footer.ownerStock,
    orders: footer.orders,
    topUps: sumBy(data.rows, (row) => row.topUps),
    redone: footer.redone,
    noPrice: canFix ? clientRows.reduce((sum, row) => sum + (row.noPrice ?? 0), 0) : null,
    segments: groups.map((group) => ({
      id: group.id,
      name: group.name,
      labelKey: group.labelKey,
      colour: group.colour,
      value: group.cents / 100,
    })),
    clientRows,
    footer,
    bill: null,
    chart: {
      series,
      ...buildBuckets(data.period.from, data.period.to, data.daily, series),
    },
  };
}

/**
 * One bill's money parts, the bill's own sections in this order: Printing,
 * Pick and pack, {owner} stock used, Not shipped, still charged (cancelled and
 * artwork print charges and garments), and Extra services (turned-on services
 * and hand-added lines). Extra services is the remainder, so the five always
 * add up to the bill Total in whole cents (C2).
 */
function moneySegments(data: ThreePlUsageReport, clientId: string): UsageSegment[] {
  const charges = byClient(data.charges, clientId);
  const garments = byClient(data.garments, clientId);
  const lines = byClient(data.lines, clientId);
  const chargeCents = (...ids: string[]) =>
    sumCents(charges.filter((charge) => ids.includes(charge.line)).map((charge) => charge.amount));
  const garmentCents = (...ids: string[]) =>
    sumCents(garments.filter((garment) => ids.includes(garment.line)).map((garment) => garment.amount));
  const printing = chargeCents('printing');
  const pickPack = chargeCents('pickPack');
  const ownerStock = garmentCents('ownerStock');
  const notShipped =
    chargeCents('cancelledPrint', 'artworkPrint') + garmentCents('cancelledGarment', 'artworkGarment');
  const all =
    sumCents(charges.map((charge) => charge.amount)) +
    sumCents(garments.map((garment) => garment.amount)) +
    sumCents(lines.map((line) => line.amount));
  const cents: Record<(typeof MONEY_PARTS)[number]['id'], number> = {
    printing,
    pickPack,
    ownerStock,
    notShipped,
    extra: all - printing - pickPack - ownerStock - notShipped,
  };
  return MONEY_PARTS.map((part) => ({
    id: part.id,
    name: null,
    labelKey: part.labelKey,
    colour: part.colour,
    value: cents[part.id] / 100,
  }));
}

/** One client: the bill, the money parts of its total, and chart series by kind. */
export function buildClientView(
  data: ThreePlUsageReport,
  clientId: string,
  options: BillOptions = NO_FIX,
): UsageView {
  const clientIndex = data.clients.findIndex((client) => client.id === clientId);
  const scoped = data.clients[clientIndex];
  const rows = byClient(data.rows, clientId);
  const bill = buildBill(data, clientId, options);
  const series: ChartSeries[] = KINDS.map((kind) => ({
    dataKey: kind.id,
    name: null,
    labelKey: kind.labelKey,
    colour: KIND_COLOURS[kind.id],
    clientIds: [clientId],
    kind: kind.id,
  }));

  return {
    viewer: data.viewer,
    scope: 'one',
    client: {
      id: clientId,
      name: scoped?.name ?? '',
      colour: clientColour(Math.max(0, clientIndex)),
      orders: scoped?.ordersShipped ?? 0,
    },
    clientsById: clientsByIdOf(data),
    scopeRows: rows,
    empty:
      rows.length === 0 &&
      !data.charges.some((charge) => charge.clientOrgId === clientId) &&
      !data.lines.some((line) => line.clientOrgId === clientId),
    total: bill.pieces,
    amount: bill.total,
    ownerStock: sumBy(rows, ownerStockOf),
    orders: scoped?.ordersShipped ?? 0,
    topUps: sumBy(rows, (row) => row.topUps),
    redone: data.viewer === 'owner' ? sumBy(rows, redoOf) : null,
    noPrice: options.canFix && data.viewer === 'owner' ? noPriceLines(bill) : null,
    segments: moneySegments(data, clientId),
    clientRows: null,
    footer: null,
    bill,
    chart: {
      series,
      ...buildBuckets(data.period.from, data.period.to, data.daily, series),
    },
  };
}

/** The view for the resolved scope: All clients, or one client's bill. */
export function buildUsageView(
  data: ThreePlUsageReport,
  selectedClientId: string | null,
  options: BillOptions = NO_FIX,
): UsageView {
  const scopeId = resolveScopeClientId(data, selectedClientId);
  return scopeId === null ? buildAllClientsView(data, options) : buildClientView(data, scopeId, options);
}

// ---------------------------------------------------------------------------
// SKU table sort (two states: a new column takes its default direction, a
// second click flips it)
// ---------------------------------------------------------------------------

export const SKU_SORT_FIELDS = [
  'client',
  'sku',
  'product',
  /** "This line": the filtered bill line's pieces on the SKU; only while a line filter is on (C2). */
  'line',
  'shipped',
  'printed',
  'pickPack',
  'blank',
  'ownerStock',
  'notShipped',
  'amount',
] as const;
export type SkuSortField = (typeof SKU_SORT_FIELDS)[number];
export type SortDir = 'asc' | 'desc';

/** The sort with no bill line filter on; see `defaultSkuSort`. */
export const DEFAULT_SKU_SORT: SkuSortField = 'shipped';

export function isSkuSortField(value: string | null | undefined): value is SkuSortField {
  return value != null && (SKU_SORT_FIELDS as readonly string[]).includes(value);
}

/** Text columns start A to Z; count and money columns start largest first. */
export function defaultSortDir(field: SkuSortField): SortDir {
  return field === 'client' || field === 'sku' || field === 'product' ? 'asc' : 'desc';
}

/**
 * The sort the table falls back to: "This line" while a bill line filters it
 * (a not-shipped line's SKUs mostly read 0 under Shipped), else Shipped.
 */
export function defaultSkuSort(line: BillLineId | null): SkuSortField {
  return line !== null ? 'line' : DEFAULT_SKU_SORT;
}

export interface SkuSortContext {
  /** All clients: the only scope with a Client column. */
  allScope: boolean;
  /** The bill line filtering the table: the only time This line exists. */
  line: BillLineId | null;
}

const isSortDir = (value: string | null): value is SortDir => value === 'asc' || value === 'desc';

/**
 * The SKU table's sort from the URL (`usort`, `udir`) for what is on screen.
 * No `usort` is the user not having chosen: `defaultSkuSort`, at its own
 * direction. A field that means nothing here (`client` off All clients, `line`
 * without a line filter, anything unknown) falls back the same way, and never
 * keeps the dropped field's `udir`. A lone `udir` is how step 1 wrote Shipped
 * flipped, so it still reads as Shipped.
 */
export function resolveSkuSort(
  sortParam: string | null,
  dirParam: string | null,
  { allScope, line }: SkuSortContext,
): { field: SkuSortField; dir: SortDir } {
  const fallback = defaultSkuSort(line);
  if (sortParam === null) {
    return isSortDir(dirParam)
      ? { field: DEFAULT_SKU_SORT, dir: dirParam }
      : { field: fallback, dir: defaultSortDir(fallback) };
  }
  const fits =
    isSkuSortField(sortParam) && (sortParam !== 'client' || allScope) && (sortParam !== 'line' || line !== null);
  if (!fits) return { field: fallback, dir: defaultSortDir(fallback) };
  return { field: sortParam, dir: isSortDir(dirParam) ? dirParam : defaultSortDir(sortParam) };
}

/**
 * The URL for a sort the user picked: nothing when it is the default for what
 * is on screen (so it follows the line filter on and off), else the field,
 * with `udir` only when it is not that field's own direction.
 */
export function skuSortParams(
  field: SkuSortField,
  dir: SortDir,
  line: BillLineId | null,
): { usort: SkuSortField | null; udir: SortDir | null } {
  if (field === defaultSkuSort(line) && dir === defaultSortDir(field)) return { usort: null, udir: null };
  return { usort: field, udir: dir === defaultSortDir(field) ? null : dir };
}

function sortValue(
  row: ThreePlUsageRow,
  field: SkuSortField,
  clientsById: ReadonlyMap<string, { name: string }>,
  line: BillLineId | null,
): number | string {
  switch (field) {
    case 'client':
      return clientsById.get(row.clientOrgId)?.name ?? '';
    case 'line':
      return line ? lineCount(row, line) : 0;
    case 'sku':
      return row.sku;
    case 'product':
      return row.title ?? '';
    case 'shipped':
      return row.shipped;
    case 'printed':
    case 'pickPack':
    case 'blank':
      return kindQty(row, field);
    case 'ownerStock':
      return ownerStockOf(row);
    case 'notShipped':
      return notShippedOf(row);
    case 'amount':
      return toCents(row.amount);
  }
}

/** Sort a copy; ties fall back to SKU, then client name, both A to Z. `line`: the bill line filter, for `line`. */
export function sortSkuRows(
  rows: readonly ThreePlUsageRow[],
  field: SkuSortField,
  dir: SortDir,
  clientsById: ReadonlyMap<string, { name: string }>,
  line: BillLineId | null = null,
): ThreePlUsageRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  const clientName = (row: ThreePlUsageRow) => clientsById.get(row.clientOrgId)?.name ?? '';
  return [...rows].sort((a, b) => {
    const av = sortValue(a, field, clientsById, line);
    const bv = sortValue(b, field, clientsById, line);
    const primary =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));
    if (primary !== 0) return primary * sign;
    return a.sku.localeCompare(b.sku) || clientName(a).localeCompare(clientName(b));
  });
}

/** Case-insensitive match on SKU and product title. */
export function searchRows(rows: readonly ThreePlUsageRow[], query: string): ThreePlUsageRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...rows];
  return rows.filter((row) => `${row.sku} ${row.title ?? ''}`.toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// SKU table column widths: a SKU code is always readable in full
// ---------------------------------------------------------------------------

/** The SKU column is never narrower than this many characters (its header). */
export const SKU_COLUMN_MIN_CHARS = 8;
/**
 * Nor wider: a longer code wraps inside the column instead of squeezing
 * Product. At this cap All clients, and one bill with This line, still fit a
 * 1280px window with the sidebar open (`skuTableMinWidthRem`).
 */
export const SKU_COLUMN_MAX_CHARS = 24;

/**
 * Characters the SKU column holds: the longest code on the table (every row
 * in scope, so filtering or searching never makes the column jump), between
 * the min and the cap.
 */
export function skuColumnChars(rows: readonly ThreePlUsageRow[]): number {
  const longest = rows.reduce((max, row) => Math.max(max, row.sku.length), 0);
  return Math.min(Math.max(longest, SKU_COLUMN_MIN_CHARS), SKU_COLUMN_MAX_CHARS);
}

/**
 * Every fixed column except SKU and Product, in rem (the table's `colgroup`,
 * P6): Shipped 4.25, Printed 4, Pick and pack 4, Blank 4, On {owner} stock
 * 5.25, Not shipped, still charged 5.75, Amount 6; All clients adds Client
 * 7.25 (the name truncates, full name in `title`). Each is bound by its widest
 * plausible value (bold "12,345", "$12,345.67") or its longest header line
 * plus `px-1.5`, measured in headless Chromium on Linux fonts (wider than
 * Segoe UI). The sort glyph sits UNDER its label, so it costs no width.
 */
const SKU_TABLE_FIXED_REM = { one: 33.25, all: 40.5 } as const;
/** The "This line" column a line filter adds (C2), in rem: a 2-line header and a bold count. */
export const SKU_LINE_COLUMN_REM = 4.25;
/** Product gives way first, down to this. */
const PRODUCT_MIN_REM = 4.5;
/** The SKU cell's side padding (`px-3`), in rem. */
const SKU_CELL_PADDING_REM = 1.5;
/**
 * One character of the SKU font (`font-mono text-xs`, 12px) in rem: 0.6em,
 * the widest monospace advance in the stack. The column itself is sized in
 * `ch` of that font, so this only has to keep Product's minimum.
 */
const SKU_CHAR_REM = 0.452;

/**
 * The SKU table's minimum width in rem: the fixed columns (plus "This line"
 * while a bill line filters the table), the SKU column at `chars`, and Product
 * at its minimum. Below it the table scrolls inside its own region, never the
 * page.
 *
 * Budget (P6): 1280 minus the 16rem sidebar minus `lg:px-8` is 960px; minus a
 * 17px page scrollbar, the region's 2px border and its own 17px scrollbar
 * (Windows classic, the floor PCs) is 924px = 57.75rem. At the 24-character
 * cap All clients needs 57.5rem and one bill with This line 54.5rem.
 */
export function skuTableMinWidthRem(scope: 'one' | 'all', chars: number, lineColumn = false): number {
  const rem =
    SKU_TABLE_FIXED_REM[scope] +
    (lineColumn ? SKU_LINE_COLUMN_REM : 0) +
    PRODUCT_MIN_REM +
    SKU_CELL_PADDING_REM +
    chars * SKU_CHAR_REM;
  return Math.ceil(rem * 4) / 4;
}
